// Web Audio scheduled playback. Two paths:
//
//   1. AudioWorklet (preferred): the worklet emits one continuous stream
//      and copies samples into the output at exact offsets — adjacent
//      frames are sample-perfect at their boundaries. Available only in
//      secure contexts (HTTPS or localhost) per the spec.
//
//   2. BufferSource batching (fallback): 5 frames per 100 ms chunk
//      scheduled with start(when). Works in every browser, including
//      Chrome at a non-secure LAN URL where AudioWorklet is unavailable.
//
// Both paths use anchor-based chaining (see below) so the test tone
// plays without any boundary clicks.
//
// We probe the worklet at construction time. The worklet sends a
// "ready" message from inside its first process() call — proof that the
// audio thread actually runs it. If we don't see "ready" within 1.5 s,
// the worklet is disposed and the batching path takes over.
//
// During the probe window, frames are routed to the batching path so
// audio starts immediately.
//
// Anchor-based chaining (the key to seamless audio):
//
// Earlier we computed each chunk's startCtx independently from the
// clock-sync offset. Even with sample-snapping, theta drifting by tens
// of microseconds between consecutive computations rounds two adjacent
// chunks to non-adjacent sample positions, leaving 0–1 sample seams.
// On a continuous sine (test tone) that's an audible click every chunk.
//
// Instead: anchor the *first* scheduled frame of a run via clock-sync,
// snap that to an integer sample, then derive every subsequent frame
// as `anchorSample + framesScheduledSinceAnchor × FRAME_SAMPLES`.
// Because all of those are integer arithmetic, adjacent chunks land
// sample-exact contiguous by construction. We only re-anchor when the
// server play_at_us drifts more than ~10 ms from our extrapolation
// (source switch, controller jump) or when a chunk is dropped.
//
// Crystal drift between server and client audio clocks is bounded:
// 50 ppm means ~3 ms / minute, so the anchor stays valid for many
// minutes before `ahead` wanders out of range, at which point a single
// re-anchor produces a tiny click that's lost in any non-trivial audio.

import type { ClockSync } from './clockSync'

export type Channel = 'mono' | 'left' | 'right'

export interface ScheduledFrame {
  seq: number
  play_at_us: bigint
  samples: Float32Array // interleaved stereo, 1920 floats per 20 ms frame
}

export interface SchedulerMetrics {
  scheduled_count: number
  late_drop_count: number
  underrun_count: number
  p95_arrival_margin_us: number
  mode: 'init' | 'worklet' | 'batch'
}

const SAMPLE_RATE = 48_000
const FRAME_SAMPLES = 960
const FRAME_DURATION_US = 20_000n
const MAX_AHEAD_SEC = 0.6
const MIN_AHEAD_SEC = 0

// 5 frames × 20 ms = 100 ms per scheduled chunk in the fallback path.
const CHUNK_FRAMES = 5
const CHUNK_SAMPLES = CHUNK_FRAMES * FRAME_SAMPLES
const CONTIGUOUS_DRIFT_US = 2_000n
const POOL_SIZE = 12
const PARTIAL_FLUSH_MS = 100

// If the observed first-frame play_at_us drifts more than this from the
// anchor extrapolation, we throw the anchor away and re-derive from
// clock-sync. ~10 ms is well above normal theta noise (sub-millisecond
// after the initial probe burst settles) and well below any source-
// switch / controller-bump artifact, so this catches the bad cases
// without false-positiving in steady state.
const ANCHOR_DRIFT_MAX_US = 10_000n

// How long to wait for the worklet to call process() before giving up
// on it.
const WORKLET_READY_TIMEOUT_MS = 1500

type Mode = 'init' | 'worklet' | 'batch'

export class Scheduler {
  private metrics: SchedulerMetrics = {
    scheduled_count: 0,
    late_drop_count: 0,
    underrun_count: 0,
    p95_arrival_margin_us: 0,
    mode: 'init',
  }
  private margins: number[] = []
  private gainNode: GainNode
  private clock: ClockSync
  private ctx: AudioContext
  private lastOutputTs: { contextTime: number; performanceTime: number } | null = null
  private outputTsRefreshAt = 0
  private deviceChangeListener: () => void

  // Render state mirrored from server Control frames.
  private volume = 1.0
  private muted = false
  private channel: Channel = 'mono'
  private latencyOffsetUs = 0

  // ─── Mode ────────────────────────────────────────────────────────────
  private mode: Mode = 'init'
  private worklet: AudioWorkletNode | null = null

  // ─── Anchor (shared between worklet + batch paths) ──────────────────
  // Integer sample index in AudioContext time that anchors the current
  // run. `framesScheduledSinceAnchor` is the count of audio frames (not
  // chunks) we've scheduled relative to the anchor.
  private anchorSample: number | null = null
  private anchorPlayAtUs: bigint | null = null
  private framesScheduledSinceAnchor = 0

  // ─── Batching state ──────────────────────────────────────────────────
  private bufferPool: AudioBuffer[] = []
  private poolCursor = 0
  private leftScratch = new Float32Array(CHUNK_SAMPLES)
  private rightScratch = new Float32Array(CHUNK_SAMPLES)
  private chunkBuffer = new Float32Array(CHUNK_SAMPLES * 2)
  private chunkFrameCount = 0
  private chunkStartPlayAtUs: bigint | null = null
  private partialFlushTimer: ReturnType<typeof setTimeout> | null = null

  constructor(ctx: AudioContext, clock: ClockSync) {
    this.ctx = ctx
    this.clock = clock
    this.gainNode = ctx.createGain()
    this.gainNode.gain.value = 1.0
    this.gainNode.connect(ctx.destination)
    for (let i = 0; i < POOL_SIZE; i++) {
      this.bufferPool.push(ctx.createBuffer(2, CHUNK_SAMPLES, SAMPLE_RATE))
    }

    this.deviceChangeListener = () => this.handleDeviceChange()
    if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeListener)
    }
    if (this.ctx && (this.ctx as any).addEventListener) {
      (this.ctx as any).addEventListener('sinkchange', this.deviceChangeListener)
    }

    void this.probeWorklet()
  }

  destroy() {
    if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
      navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeListener)
    }
    if (this.ctx && (this.ctx as any).removeEventListener) {
      (this.ctx as any).removeEventListener('sinkchange', this.deviceChangeListener)
    }
  }

  getOutputLatencyMs(): number {
    const lat = this.ctx.outputLatency ?? (this.ctx as any).baseLatency ?? 0
    return Math.round(lat * 1000)
  }

  private handleDeviceChange() {
    this.lastOutputTs = null
    this.resetAnchor()
    // eslint-disable-next-line no-console
    console.info(
      `[Multispeaker scheduler] Output device change detected. Resetting anchor. outputLatency=${(this.ctx.outputLatency ?? 0) * 1000}ms`
    )
  }

  /** Kept so callers don't need to branch. Resolves immediately. */
  whenReady(): Promise<void> {
    return Promise.resolve()
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v))
    this.applyGain()
  }

  setMuted(muted: boolean) {
    this.muted = muted
    this.applyGain()
  }

  setChannel(channel: Channel) {
    if (this.mode === 'batch' && this.chunkFrameCount > 0 && channel !== this.channel) {
      this.flushChunk()
    }
    this.channel = channel
    this.worklet?.port.postMessage({ type: 'state', channel })
  }

  setLatencyOffsetUs(offsetUs: number) {
    this.latencyOffsetUs = Math.trunc(offsetUs)
    // Latency-offset changes shift every future startCtx, so the anchor
    // built under the old offset no longer applies. Force re-anchor.
    this.resetAnchor()
  }

  ingest(frame: ScheduledFrame) {
    const before = this.metrics.scheduled_count
    const beforeDrop = this.metrics.late_drop_count
    if (this.mode === 'worklet') {
      this.ingestWorklet(frame)
    } else {
      this.ingestBatch(frame)
    }
    if (!this.loggedFirstDecision) {
      const scheduled = this.metrics.scheduled_count > before
      const dropped = this.metrics.late_drop_count > beforeDrop
      if (scheduled || dropped) {
        this.loggedFirstDecision = true
        const clockState = this.clock.state()
        // eslint-disable-next-line no-console
        console.info(
          `[Multispeaker scheduler] first frame ${scheduled ? 'SCHEDULED' : 'DROPPED'} in mode=${this.mode}, ` +
          `ctx.state=${this.ctx.state}, ctx.currentTime=${this.ctx.currentTime.toFixed(3)}, ` +
          `clock=${clockState ? `theta=${clockState.theta_us}us, dmin=${clockState.delta_min_us}us, probes=${clockState.probes_in_window}` : 'NOT_READY'}, ` +
          `play_at_us=${frame.play_at_us}, samples=${frame.samples.length}`,
        )
      }
    }
  }
  private loggedFirstDecision = false

  getMetrics(): SchedulerMetrics {
    return {
      ...this.metrics,
      p95_arrival_margin_us: this.computeP95(),
      mode: this.mode,
    }
  }

  resetMetricsCounters() {
    this.metrics.late_drop_count = 0
    this.metrics.underrun_count = 0
    this.metrics.scheduled_count = 0
  }

  // ─── Worklet probe ────────────────────────────────────────────────────

  private async probeWorklet() {
    if (!('audioWorklet' in this.ctx) || typeof AudioWorkletNode === 'undefined') {
      this.commitMode('batch', 'AudioWorklet API unavailable')
      return
    }
    let node: AudioWorkletNode | null = null
    try {
      await this.ctx.audioWorklet.addModule('/playback-worklet.js')
      node = new AudioWorkletNode(this.ctx, 'playback', {
        numberOfOutputs: 1,
        outputChannelCount: [2],
      })

      const ready = new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
          if (node) node.port.onmessage = null
          resolve(false)
        }, WORKLET_READY_TIMEOUT_MS)
        node!.port.onmessage = (ev) => {
          if (ev.data?.type === 'ready') {
            clearTimeout(timeout)
            node!.port.onmessage = null
            resolve(true)
          }
        }
      })

      node.connect(this.gainNode)
      const ok = await ready

      if (ok && node) {
        this.worklet = node
        node.port.postMessage({ type: 'state', channel: this.channel })
        this.commitMode('worklet', 'AudioWorklet ready')
        // Mode change resets which path frames flow through; anchor
        // from the previous (init/batch) path no longer applies.
        this.resetAnchor()
      } else {
        try { node?.disconnect() } catch { /* noop */ }
        this.commitMode('batch', 'worklet did not call process() within timeout')
      }
    } catch (e) {
      try { node?.disconnect() } catch { /* noop */ }
      this.commitMode('batch', `worklet load failed: ${e instanceof Error ? e.message : e}`)
    }
  }

  private commitMode(mode: Mode, why: string) {
    if (this.mode === mode) return
    this.mode = mode
    // eslint-disable-next-line no-console
    console.info(`[Multispeaker] playback mode = ${mode} (${why})`)
  }

  // ─── Worklet ingest ───────────────────────────────────────────────────

  private ingestWorklet(frame: ScheduledFrame) {
    const startCtx = this.resolveStartCtx(frame.play_at_us, /* frameCount */ 1)
    if (startCtx === null) return // already counted in late_drop_count

    const ahead = startCtx - this.ctx.currentTime
    this.recordMargin(ahead * 1_000_000)
    if (ahead <= MIN_AHEAD_SEC || ahead > MAX_AHEAD_SEC) {
      this.metrics.late_drop_count++
      this.resetAnchor()
      return
    }
    this.worklet!.port.postMessage(
      { type: 'frame', startCtx, samples: frame.samples },
      [frame.samples.buffer],
    )
    this.metrics.scheduled_count++
    this.framesScheduledSinceAnchor += 1
  }

  // ─── Batching ingest (fallback) ───────────────────────────────────────

  private ingestBatch(frame: ScheduledFrame) {
    if (this.chunkStartPlayAtUs === null) {
      this.chunkStartPlayAtUs = frame.play_at_us
    } else {
      const expected =
        this.chunkStartPlayAtUs + BigInt(this.chunkFrameCount) * FRAME_DURATION_US
      const drift =
        frame.play_at_us > expected ? frame.play_at_us - expected : expected - frame.play_at_us
      if (drift > CONTIGUOUS_DRIFT_US) {
        // Source switch or controller bump — flush what we have, drop
        // the anchor because subsequent chunks aren't on its grid.
        this.flushChunk()
        this.resetAnchor()
        this.chunkStartPlayAtUs = frame.play_at_us
      }
    }

    const offset = this.chunkFrameCount * FRAME_SAMPLES * 2
    this.chunkBuffer.set(frame.samples, offset)
    this.chunkFrameCount++

    if (this.chunkFrameCount >= CHUNK_FRAMES) {
      this.flushChunk()
    } else {
      this.armPartialFlush()
    }
  }

  private flushChunk() {
    this.clearPartialFlush()
    const frameCount = this.chunkFrameCount
    const startUs = this.chunkStartPlayAtUs
    if (frameCount === 0 || startUs === null) {
      this.chunkStartPlayAtUs = null
      this.chunkFrameCount = 0
      return
    }

    const startCtx = this.resolveStartCtx(startUs, frameCount)
    if (startCtx === null) {
      this.resetChunkBuf()
      return
    }

    const ahead = startCtx - this.ctx.currentTime
    this.recordMargin(ahead * 1_000_000)
    if (ahead <= MIN_AHEAD_SEC || ahead > MAX_AHEAD_SEC) {
      this.metrics.late_drop_count += frameCount
      this.resetAnchor()
      this.resetChunkBuf()
      return
    }

    const samples = frameCount * FRAME_SAMPLES
    const left = this.leftScratch
    const right = this.rightScratch
    const src = this.chunkBuffer

    switch (this.channel) {
      case 'mono': {
        for (let i = 0; i < samples; i++) {
          const m = 0.5 * (src[i * 2] + src[i * 2 + 1])
          left[i] = m
          right[i] = m
        }
        break
      }
      case 'left': {
        for (let i = 0; i < samples; i++) {
          const l = src[i * 2]
          left[i] = l
          right[i] = l
        }
        break
      }
      case 'right': {
        for (let i = 0; i < samples; i++) {
          const r = src[i * 2 + 1]
          left[i] = r
          right[i] = r
        }
        break
      }
    }
    if (samples < CHUNK_SAMPLES) {
      left.fill(0, samples)
      right.fill(0, samples)
    }

    const ab = this.bufferPool[this.poolCursor]
    this.poolCursor = (this.poolCursor + 1) % POOL_SIZE
    ab.copyToChannel(left, 0)
    ab.copyToChannel(right, 1)

    const node = this.ctx.createBufferSource()
    node.buffer = ab
    node.connect(this.gainNode)
    node.start(startCtx, 0, samples / SAMPLE_RATE)
    this.metrics.scheduled_count += frameCount
    this.framesScheduledSinceAnchor += frameCount

    this.resetChunkBuf()
  }

  private resetChunkBuf() {
    this.chunkFrameCount = 0
    this.chunkStartPlayAtUs = null
  }

  private armPartialFlush() {
    if (this.partialFlushTimer) return
    this.partialFlushTimer = setTimeout(() => {
      this.partialFlushTimer = null
      if (this.chunkFrameCount > 0) this.flushChunk()
    }, PARTIAL_FLUSH_MS)
  }

  private clearPartialFlush() {
    if (this.partialFlushTimer) {
      clearTimeout(this.partialFlushTimer)
      this.partialFlushTimer = null
    }
  }

  // ─── Anchor / startCtx resolution ────────────────────────────────────

  /**
   * Decide on the AudioContext start time for the frame (or chunk's first
   * frame) at server-time `playAtUs`. Either extrapolates from the active
   * anchor (sample-exact continuation of the previous chunk) or
   * establishes a fresh anchor via clock-sync.
   *
   * `frameCount` is informational — used only to debit the metrics
   * counter when the function returns null (clock not ready).
   */
  private resolveStartCtx(playAtUs: bigint, frameCount: number): number | null {
    const adjustedServerUs = playAtUs + BigInt(this.latencyOffsetUs)

    // Try anchor extrapolation first.
    if (this.anchorSample !== null && this.anchorPlayAtUs !== null) {
      // Check if the anchor's timeline has drifted from the clock-sync's current estimate.
      const playAtPerfUs = this.clock.serverToPerfUs(adjustedServerUs)
      const ts = playAtPerfUs !== null ? this.outputTimestamp() : null
      if (playAtPerfUs !== null && ts !== null) {
        const playAtPerfMs = Number(playAtPerfUs) / 1000
        const expectedCtxTime = ts.contextTime + (playAtPerfMs - ts.performanceTime) / 1000
        const anchorCtxTime = (this.anchorSample + this.framesScheduledSinceAnchor * FRAME_SAMPLES) / SAMPLE_RATE
        const syncDriftUs = Math.abs(anchorCtxTime - expectedCtxTime) * 1_000_000
        
        // If the anchor's playback time is more than 1.5 ms away from the clock sync,
        // re-anchor to the updated clock sync time.
        if (syncDriftUs > 1500) {
          // eslint-disable-next-line no-console
          console.info(
            `[Multispeaker scheduler] Anchor drift relative to ClockSync: ${Math.round(syncDriftUs)} us. Re-anchoring.`
          )
          this.resetAnchor()
        }
      }
    }

    if (this.anchorSample !== null && this.anchorPlayAtUs !== null) {
      const expected =
        this.anchorPlayAtUs + BigInt(this.framesScheduledSinceAnchor) * FRAME_DURATION_US
      const drift = adjustedServerUs > expected
        ? adjustedServerUs - expected
        : expected - adjustedServerUs
      if (drift <= ANCHOR_DRIFT_MAX_US) {
        // Exact integer arithmetic — no float drift, no sub-sample seam.
        const sample = this.anchorSample + this.framesScheduledSinceAnchor * FRAME_SAMPLES
        return sample / SAMPLE_RATE
      }
      // Drift too big — anchor is stale (source switch, controller jump,
      // crystal drift hit the cap). Throw it away.
      this.resetAnchor()
    }

    // Establish a fresh anchor from clock-sync.
    const playAtPerfUs = this.clock.serverToPerfUs(adjustedServerUs)
    if (playAtPerfUs === null) {
      this.metrics.late_drop_count += frameCount
      return null
    }
    const ts = this.outputTimestamp()
    if (!ts) {
      this.metrics.late_drop_count += frameCount
      return null
    }
    const playAtPerfMs = Number(playAtPerfUs) / 1000
    const startCtxRaw = ts.contextTime + (playAtPerfMs - ts.performanceTime) / 1000
    const anchorSample = Math.round(startCtxRaw * SAMPLE_RATE)

    this.anchorSample = anchorSample
    this.anchorPlayAtUs = adjustedServerUs
    this.framesScheduledSinceAnchor = 0
    return anchorSample / SAMPLE_RATE
  }

  private resetAnchor() {
    this.anchorSample = null
    this.anchorPlayAtUs = null
    this.framesScheduledSinceAnchor = 0
  }

  // ─── Shared utilities ────────────────────────────────────────────────

  private applyGain() {
    this.gainNode.gain.setTargetAtTime(
      this.muted ? 0 : this.volume,
      this.ctx.currentTime,
      0.01,
    )
  }

  private outputTimestamp(): { contextTime: number; performanceTime: number } | null {
    const now = performance.now()
    if (!this.lastOutputTs || now - this.outputTsRefreshAt > 1000) {
      const fn = (this.ctx as { getOutputTimestamp?: () => { contextTime: number; performanceTime: number } }).getOutputTimestamp?.bind(this.ctx)
      if (fn) {
        const t = fn()
        if (t && t.performanceTime !== undefined && t.contextTime !== undefined) {
          this.lastOutputTs = { contextTime: t.contextTime, performanceTime: t.performanceTime }
        }
      }
      if (!this.lastOutputTs) {
        const outLat = (this.ctx as { outputLatency?: number }).outputLatency ?? this.ctx.baseLatency ?? 0
        this.lastOutputTs = {
          contextTime: this.ctx.currentTime,
          performanceTime: now + outLat * 1000,
        }
      }
      this.outputTsRefreshAt = now
    }
    return this.lastOutputTs
  }

  private recordMargin(us: number) {
    this.margins.push(us)
    if (this.margins.length > 64) this.margins.shift()
  }

  private computeP95(): number {
    if (this.margins.length === 0) return 0
    const sorted = [...this.margins].sort((a, b) => a - b)
    const idx = Math.max(0, Math.floor(sorted.length * 0.05))
    return Math.round(sorted[idx])
  }
}
